import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Copy, Plug, ShieldAlert } from "lucide-react";

const DEFAULT_PROD_BASE = "https://admin.navscora.com/api/integrations/v1";
const PREVIEW_BASE = `${(typeof window !== "undefined" ? window.location.origin : "")}/api/integrations/v1`;
const KEY_PLACEHOLDER = "YOUR_NAVSCORA_INTEGRATION_KEY";

const copy = (text, msg = "Copied") => { navigator.clipboard.writeText(text); toast.success(msg); };

function Code({ children, testId }) {
  const text = typeof children === "string" ? children : "";
  return (
    <div className="group relative">
      <pre className="overflow-x-auto rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3 pr-10 text-[11px] leading-relaxed text-cyan-100" data-testid={testId}>{children}</pre>
      <Button size="icon" variant="ghost" className="absolute right-1.5 top-1.5 h-7 w-7 text-slate-400 opacity-0 transition group-hover:opacity-100"
        onClick={() => copy(text, "Snippet copied")} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

export function ConnectGuide({ open, onOpenChange, integration }) {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, enabled: open });
  if (!integration) return null;
  const id = integration.id;
  const prefix = integration.key || "product";
  const PROD_BASE = settings?.integration_base_url || DEFAULT_PROD_BASE;
  const envExample = `# ${integration.name} backend secrets / environment variables (NEVER in frontend)
NAVSCORA_BASE_URL=${PROD_BASE}
NAVSCORA_INTEGRATION_ID=${id}
NAVSCORA_INTEGRATION_KEY=${KEY_PLACEHOLDER}`;
  const healthExample = `curl -sS -X POST "$NAVSCORA_BASE_URL/health" \\
  -H "X-Integration-Id: $NAVSCORA_INTEGRATION_ID" \\
  -H "X-Integration-Key: $NAVSCORA_INTEGRATION_KEY" \\
  -H "X-Timestamp: $(date +%s)" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"ok","version":"2.3.1"}'`;
  const usageExample = `curl -sS -X POST "$NAVSCORA_BASE_URL/usage" \\
  -H "X-Integration-Id: $NAVSCORA_INTEGRATION_ID" \\
  -H "X-Integration-Key: $NAVSCORA_INTEGRATION_KEY" \\
  -H "X-Timestamp: $(date +%s)" \\
  -H "Content-Type: application/json" \\
  -d '{"period":"2026-06",
       "idempotency_key":"${prefix}:acme:default:production:2026-06:usage-v1",
       "tenant_id":"acme","product_instance_id":"default","environment":"production",
       "metrics":{"vessels":12,"active_users":84}}'`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-[#1E2D4A] bg-[#111A2E] text-slate-100" data-testid="connect-guide-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plug className="h-4 w-4 text-cyan-400" /> {integration.name} — Connect Guide</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm text-slate-300">
          {/* Base URLs */}
          <div>
            <SectionTitle className="mb-2">Base URLs</SectionTitle>
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2">
                <div><div className="text-[10px] uppercase text-emerald-400">Production</div><div className="font-mono-fin break-all text-xs text-slate-200" data-testid="guide-prod-url">{PROD_BASE}</div></div>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-slate-400" onClick={() => copy(PROD_BASE)} data-testid="copy-prod-url"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                <div><div className="text-[10px] uppercase text-amber-400">Preview — non-production (testing only)</div><div className="font-mono-fin break-all text-xs text-slate-200" data-testid="guide-preview-url">{PREVIEW_BASE}</div></div>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-slate-400" onClick={() => copy(PREVIEW_BASE)} data-testid="copy-preview-url"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>

          {/* Credentials */}
          <div>
            <SectionTitle className="mb-2">Credentials & headers</SectionTitle>
            <div className="space-y-2 rounded-lg border border-[#1E2D4A] bg-[#0B1120] p-3">
              <div className="text-[10px] uppercase text-slate-500">X-Integration-Id</div>
              <div className="flex items-center gap-2">
                <div className="font-mono-fin break-all text-xs text-slate-200" data-testid="guide-integration-id">{id}</div>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-slate-400" onClick={() => copy(id, "Integration ID copied")} data-testid="copy-integration-id"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="mt-2 text-[10px] uppercase text-slate-500">X-Integration-Key</div>
              <div className="font-mono-fin break-all text-xs text-amber-300" data-testid="guide-key-placeholder">{KEY_PLACEHOLDER}</div>
            </div>
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-rose-500/20 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-200" data-testid="guide-secret-warning">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              The secret key is shown only once, at generation. It is never redisplayed here. Store it only in {integration.name} backend secrets / environment variables — never in frontend or browser code. If lost, rotate the key.
            </div>
          </div>

          {/* Env vars */}
          <div><SectionTitle className="mb-2">Environment variables</SectionTitle><Code testId="guide-env-example">{envExample}</Code></div>

          {/* Test connection */}
          <div>
            <SectionTitle className="mb-2">Test connection (health first)</SectionTitle>
            <p className="mb-2 text-xs text-slate-400">A successful <span className="text-cyan-300">/health</span> call flips this product to Connected and starts the freshness clock. Production requests must include an <span className="text-cyan-300">X-Timestamp</span> header (epoch seconds, ±5 min).</p>
            <Code testId="guide-health-example">{healthExample}</Code>
          </div>

          {/* Usage */}
          <div>
            <SectionTitle className="mb-2">Send usage</SectionTitle>
            <Code testId="guide-usage-example">{usageExample}</Code>
          </div>

          {/* Idempotency */}
          <div>
            <SectionTitle className="mb-2">Idempotency & mapping</SectionTitle>
            <ul className="list-disc space-y-1 pl-5 text-xs text-slate-400">
              <li>Every <span className="text-cyan-300">/usage</span> call needs an <span className="text-cyan-300">idempotency_key</span> in the form <span className="font-mono-fin">{prefix}:&lt;tenant&gt;:&lt;instance&gt;:&lt;environment&gt;:&lt;period&gt;:usage-v1</span>. <span className="text-cyan-300">/usage</span> is a complete snapshot for that tenant/instance/environment/period — a resend replaces it (metrics are not accumulated) and guards against replays.</li>
              <li>Product-supplied <span className="font-mono-fin">tenant_id</span>/<span className="font-mono-fin">customer_id</span> are treated as external IDs. Map them to a NavScora customer under <span className="text-slate-200">Mappings</span>. Unmapped production usage is held as <span className="text-amber-300">Needs Review</span> and excluded from finance; once you add the mapping it is auto-resolved to Mapped — no resend needed.</li>
            </ul>
          </div>

          {/* Errors */}
          <div>
            <SectionTitle className="mb-2">Error codes</SectionTitle>
            <div className="grid grid-cols-2 gap-1.5 text-xs text-slate-400">
              <div><span className="font-mono-fin text-slate-200">400</span> bad request / missing key / disallowed field</div>
              <div><span className="font-mono-fin text-slate-200">401</span> invalid or revoked credentials</div>
              <div><span className="font-mono-fin text-slate-200">413</span> body too large (max 64 KB)</div>
              <div><span className="font-mono-fin text-slate-200">429</span> rate limit exceeded (retry after 60s)</div>
            </div>
          </div>

          {/* Rotation */}
          <div>
            <SectionTitle className="mb-2">Rotation & revocation</SectionTitle>
            <ul className="list-disc space-y-1 pl-5 text-xs text-slate-400">
              <li><span className="text-slate-200">Rotate Key</span> issues a new secret and invalidates the old one — update {integration.name} secrets immediately.</li>
              <li><span className="text-slate-200">Revoke</span> disables all API access and returns the product to Not Connected.</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
